/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (this.shadowRoot) return;

    const template = this.querySelector(':scope > template[shadowrootmode="open"]');

    if (!(template instanceof HTMLTemplateElement)) return;

    const shadow = this.attachShadow({ mode: 'open' });
    shadow.append(template.content.cloneNode(true));
  }
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) {
        callback(entries, this);
        return;
      }

      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates.
 *
 * @extends {Event}
 */
export class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent.
   *
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached.
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });

    this.detail = {
      minimumReached,
    };
  }
}

export class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position.
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });

    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 *
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 *
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'minimum-items') {
      this.#minimumItemsCache = undefined;
    }

    if (name !== 'disabled' || !this.#initialized) return;

    if (newValue === 'true') {
      this.#reset();
    } else {
      this.#reflowItems();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#initialized) return;

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        if (!this.isConnected) return;

        this.removeAttribute('defer');
        this.#initialize();
      };

      this.#requestIdle(() => this.schedule(deferredReflow));
      return;
    }

    if (this.shadowRoot) {
      this.#initialize();
      return;
    }

    window.setTimeout(() => {
      if (!this.isConnected) return;
      this.#initialize();
    }, 0);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.removeEventListener('reflow', this.#handleReflow);

    this.#initialized = false;
    this.#scheduled = false;
    this.#refs = undefined;
    this.#minimumItemsCache = undefined;
    this.#lastMinimumReached = null;
    this.#lastDimensions = {
      width: null,
      height: null,
    };
  }

  /**
   * Initialize the element.
   */
  #initialize() {
    if (this.#initialized) return;

    const { shadowRoot } = this;

    if (!shadowRoot) return;

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      return;
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    this.#initialized = true;
    this.addEventListener('reflow', this.#handleReflow);
    this.#reflowItems();
  }

  /**
   * Schedules a callback with the theme scheduler when available.
   *
   * @returns {(callback: () => void) => void}
   */
  get schedule() {
    const scheduler = window.Theme?.utilities?.scheduler;

    if (typeof scheduler?.schedule === 'function') {
      return scheduler.schedule;
    }

    return (callback) => {
      requestAnimationFrame(() => {
        window.setTimeout(callback, 0);
      });
    };
  }

  /**
   * @param {() => void} callback
   */
  #requestIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: 800 });
      return;
    }

    window.setTimeout(callback, 1);
  }

  /**
   * @param {ReflowEvent} event
   */
  #handleReflow = (event) => {
    this.#reflowItems(event.detail?.lastVisibleElement);
  };

  #scheduled = false;

  #initialized = false;

  /** @type {number | null | undefined} */
  #minimumItemsCache = undefined;

  /** @type {boolean | null} */
  #lastMinimumReached = null;

  /**
   * Get the minimum number of items before changing the minimum-reached state.
   *
   * @returns {number | null}
   */
  get minimumItems() {
    if (this.#minimumItemsCache !== undefined) {
      return this.#minimumItemsCache;
    }

    const value = this.getAttribute('minimum-items');

    this.#minimumItemsCache = value ? Number.parseInt(value, 10) : null;

    return this.#minimumItemsCache;
  }

  get overflowSlot() {
    return this.#refs?.overflowSlot;
  }

  get defaultSlot() {
    return this.#refs?.defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (entries) => {
    if (this.#scheduled || !this.#initialized) return;

    const firstEntry = entries?.[0];
    const isResize =
      typeof ResizeObserverEntry !== 'undefined' && firstEntry instanceof ResizeObserverEntry;

    if (isResize) {
      const width = Math.round(firstEntry.contentRect.width);
      const height = Math.round(firstEntry.contentRect.height);

      if (!width || !height) return;

      if (width === this.#lastDimensions.width && height === this.#lastDimensions.height) {
        return;
      }

      this.#lastDimensions = { width, height };
    }

    this.#scheduled = true;

    this.schedule(() => {
      try {
        if (this.isConnected && this.#initialized) {
          this.#reflowItems();
        }
      } finally {
        this.#scheduled = false;
      }
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const refs = this.#refs;
    if (!refs) return;

    const { defaultSlot, overflowSlot } = refs;
    const overflowElements = overflowSlot.assignedElements();

    for (let index = 0; index < overflowElements.length; index += 1) {
      const element = overflowElements[index];

      if (element.slot !== defaultSlot.name) {
        element.slot = defaultSlot.name;
      }
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const refs = this.#refs;
    if (!refs) return;

    const { list } = refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');

    if (this.style.getPropertyValue('--overflow-count') !== '0') {
      this.style.setProperty('--overflow-count', '0');
    }
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count.
   *
   * @param {Element[]} visibleElements - The currently visible elements.
   */
  #updateMinimumReached(visibleElements) {
    const minimumItems = this.minimumItems;

    if (minimumItems === null) return;

    const minimumReached = visibleElements.length < minimumItems;

    if (this.#lastMinimumReached === minimumReached) return;

    this.#lastMinimumReached = minimumReached;

    if (minimumReached) {
      this.setAttribute('minimum-reached', '');
    } else {
      this.removeAttribute('minimum-reached');
    }

    this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const refs = this.#refs;
    if (!refs) return;

    const { placeholder } = refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Returns elements visible in the current row.
   *
   * @param {Element[]} elements
   * @param {DOMRect} rootRect
   * @returns {Element[]}
   */
  #getVisibleElements(elements, rootRect) {
    const visibleElements = [];

    for (let index = 0; index < elements.length; index += 1) {
      const element = elements[index];

      if (element.getBoundingClientRect().top <= rootRect.top) {
        visibleElements.push(element);
      }
    }

    return visibleElements;
  }

  /**
   * Reflow items based on available space within the list.
   *
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position.
   */
  #reflowItems = (lastVisibleElement) => {
    const refs = this.#refs;

    if (!refs) return;

    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    const firstChildRect = this.firstElementChild?.getBoundingClientRect();
    const height = firstChildRect?.height || 0;

    if (height) {
      const nextHeight = `${height}px`;

      if (list.style.height !== nextHeight) {
        list.style.height = nextHeight;
      }
    }

    if (list.style.overflow !== 'hidden') {
      list.style.setProperty('overflow', 'hidden');
    }

    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    moreSlot.hidden = true;
    placeholder.hidden = true;

    const rootRect = list.getBoundingClientRect();

    this.#lastDimensions = {
      width: Math.round(rootRect.width),
      height: Math.round(rootRect.height),
    };

    let visibleElements = this.#getVisibleElements(elements, rootRect);

    if (visibleElements.length !== elements.length || lastVisibleElement) {
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;

      if (height) {
        const nextHeight = `${height}px`;

        if (moreSlot.style.height !== nextHeight) {
          moreSlot.style.height = nextHeight;
        }
      }

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      visibleElements = this.#getVisibleElements(elements, rootRect);

      moreSlot.style.removeProperty('order');

      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');

        const visibleIndex = visibleElements.indexOf(lastVisibleElement);

        if (visibleIndex !== -1) {
          visibleElements.splice(visibleIndex, 1);
          visibleElements.push(lastVisibleElement);
        }
      }

      if (moreSlot.style.height !== 'auto') {
        moreSlot.style.setProperty('height', 'auto');
      }
    }

    const visibleSet = new Set(visibleElements);
    const overflowingElements = [];
    let firstOverflowingElement = null;

    for (let index = 0; index < elements.length; index += 1) {
      const element = elements[index];

      if (!visibleSet.has(element)) {
        if (!firstOverflowingElement) {
          firstOverflowingElement = element;
        }

        overflowingElements.push(element);
      }
    }

    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    for (let index = 0; index < elements.length; index += 1) {
      const element = elements[index];
      const nextSlot = visibleSet.has(element) ? defaultSlot.name : overflowSlot.name;

      if (element.slot !== nextSlot) {
        element.slot = nextSlot;
      }
    }

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      const nextWidth = `${placeholderWidth}px`;

      if (placeholder.style.width !== nextWidth) {
        placeholder.style.width = nextWidth;
      }

      placeholder.hidden = false;
    }

    const overflowCount = `${overflowingElements.length}`;
    const counterReset = `overflow-count ${overflowingElements.length}`;

    if (list.style.getPropertyValue('counter-reset') !== counterReset) {
      list.style.setProperty('counter-reset', counterReset);
    }

    if (this.style.getPropertyValue('--overflow-count') !== overflowCount) {
      this.style.setProperty('--overflow-count', overflowCount);
    }

    list.style.removeProperty('overflow');

    this.#updateMinimumReached(visibleElements);

    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * } | undefined}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

/**
 * Function to calculate total height of header group children.
 *
 * @param {HTMLElement | null} [header]
 * @param {HTMLElement | null} [headerGroup]
 * @returns {number}
 */
export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;

  for (let index = 0; index < children.length; index += 1) {
    const element = children[index];

    if (element === header || !(element instanceof HTMLElement)) continue;

    totalHeight += element.offsetHeight;
  }

  return totalHeight;
}

/**
 * Sets a body style property only when it actually changes.
 *
 * @param {string} name
 * @param {string} value
 */
function setBodyStyleProperty(name, value) {
  if (!document.body) return;

  if (document.body.style.getPropertyValue(name) !== value) {
    document.body.style.setProperty(name, value);
  }
}

/**
 * Initialize and maintain header height CSS variables.
 * This is critical for preventing layout shifts during page load.
 * There is a `ResizeObserver` and `MutationObserver` that kicks in post hydration in header.js
 * Note: header-group uses display: contents, so we must observe all children.
 */
(() => {
  const header = document.querySelector('header-component');

  if (!(header instanceof HTMLElement)) return;

  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  setBodyStyleProperty('--header-height', `${headerHeight}px`);
  setBodyStyleProperty('--header-group-height', `${headerGroupHeight}px`);
})();

/**
 * Updates CSS custom properties for transparent header offset calculation.
 * Avoids expensive :has() selectors.
 */
(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (!(header instanceof HTMLElement) || !(headerGroup instanceof HTMLElement)) {
    setBodyStyleProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasHeaderSection = headerGroup.querySelector('.header-section');

  if (!hasHeaderSection || !header.hasAttribute('transparent')) {
    setBodyStyleProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';

  setBodyStyleProperty('--transparent-header-offset-boolean', shouldApplyOffset);
})();